import React from 'react';
import { 
  ThemeProvider, 
  createTheme, 
  Box, 
  Stack, 
  Container, 
  Typography, 
  Button, 
  TextField,
  Link
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  Groups as CommunityIcon,
  LocalLibrary as LibraryIcon,
  Coffee as CoffeeIcon,
  ArrowForward as ArrowIcon,
  Storefront as StoreIcon,
  Schedule as ClockIcon,
  Call as PhoneIcon,
  Mail as MailIcon,
  LocationOn as PinIcon
} from '@mui/icons-material';

const theme = createTheme({
  palette: {
    primary: { main: '#1773cf' },
    background: { default: '#f6f7f8' }, // Matching your background-light
  },
  typography: {
    fontFamily: '"Work Sans", sans-serif',
    h1: { fontWeight: 900, letterSpacing: '-0.03em' },
    h2: { fontWeight: 800 },
  },
});

const AboutPage: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        

        <Container sx={{ pt: { xs: 1, md: 0 } }}>
          {/* Hero */}
          <Stack spacing={6} sx={{ mb: 12 }}>
            <Box sx={{ maxWidth: 800 }}>
              <Typography variant="h1" sx={{ fontSize: { xs: '2.5rem', md: '4rem' }, mb: 2 }}>
                More than just a <Box component="span" color="primary.main">bookstore</Box>.
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '1.05rem', md: '1.35rem' }, maxWidth: 600 }}>
                We are a community of dreamers, thinkers, and storytellers. Connecting readers with stories since 2024.
              </Typography>
            </Box>
            
            <Box sx={{ 
              height: { xs: 300, md: 500 }, borderRadius: 4, overflow: 'hidden', position: 'relative',
              boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)'
            }}>
              <Box component="img" src="/img/back.jpeg" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <Box sx={{ position: 'absolute', bottom: { xs: 20, md: 32 }, left: { xs: 20, md: 32 }, right: { xs: 20, md: 'auto' }, color: 'white', zIndex: 2 }}>
                <Typography variant="subtitle1" sx={{ fontSize: { xs: '1.05rem', md: '1.4rem' }, fontWeight: 'bold', lineHeight: 1.25 }}>Visit our Qamar Books store</Typography>
                <Typography sx={{ fontSize: { xs: '0.88rem', md: '1.18rem' }, display: 'block', mt: 0.5 }} variant="caption">123 Literary Lane, Booktown, NY 10012</Typography>
              </Box>
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }} />
            </Box>
          </Stack>

          {/* Features */}
          <Grid container spacing={3} sx={{ mb: 12 }}>
            <Grid item xs={12} md={4}>
              <FeatureCard icon={<CommunityIcon />} title="Community First"
               desc="We host weekly book clubs, author readings, and children's story hours to bring people together through the love of reading." />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard icon={<LibraryIcon />} title="Curated Selection"
               desc="Forget algorithms. Our shelves are stocked with hand-picked titles by our expert staff who actually read the books they sell" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FeatureCard icon={<CoffeeIcon />} title="The Reading Cafe" 
              desc="Enjoy a fresh brew of locally sourced coffee while you dive into your new favorite book in our comfortable lounge area." />
            </Grid>
          </Grid>

          {/* History */}
          <Grid container spacing={8} alignItems="center" sx={{ mb: 12 }}>
            <Grid item xs={12} md={5}>
              <Box sx={{ borderRadius: 4, overflow: 'hidden', height: 400 }}>
                <Box component="img" src="/img/history.png" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={3}>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>Our History</Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Founded in 2024 by bibliophile Martin, Qamar Books started as a small corner shop with just two bookshelves and a dream. In a world rapidly moving towards digital media, Sarah believed in the tactile magic of paper and ink.
                  Over the decades, we've expanded three times, survived the e-book revolution, and become a beloved landmark in the city. We attribute our success to our unwavering commitment to personal service and our deep roots in the local community.
                  Today, Qamar Books is run by the second generation of the Jenkins family, continuing the legacy of fostering a love for literature in readers of all ages.
                                  </Typography>
                <Link href="#" color="primary" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                  Read full story <ArrowIcon fontSize="small" />
                </Link>
              </Stack>
            </Grid>
          </Grid>

          {/* Contact Box */}
          <Box sx={{ p: { xs: 4, md: 8 }, bgcolor: 'white', borderRadius: 4, border: '1px solid #e2e8f0' }}>
            <Grid container spacing={8}>
              <Grid item xs={12} lg={6}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Get in Touch</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>Have a question? Send us a message.</Typography>
                <Stack spacing={2.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField fullWidth label="First Name" variant="filled" />
                    <TextField fullWidth label="Last Name" variant="filled" />
                  </Stack>
                  <TextField fullWidth label="Email" variant="filled" />
                  <TextField fullWidth label="Message" multiline rows={4} variant="filled" />
                  <Button variant="contained" size="large" sx={{ alignSelf: 'start', px: 4, py: 1.5, fontWeight: 'bold' }}>
                    Send Message
                  </Button>
                </Stack>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Stack spacing={4}>
                  <Box sx={{ height: 250, borderRadius: 4, bgcolor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <PinIcon color="primary" sx={{ fontSize: 40 }} />
                  </Box>
                  <Grid container spacing={3}>
                    <ContactDetail icon={<StoreIcon />} title="Visit Us" content="123 Literary Lane, NY" />
                    <ContactDetail icon={<ClockIcon />} title="Hours" content="Mon-Fri: 9am - 8pm" />
                    <ContactDetail icon={<PhoneIcon />} title="Phone" content="(555) 123-4567" />
                    <ContactDetail icon={<MailIcon />} title="Email" content="hello@qamarbooks.com" />
                  </Grid>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

const FeatureCard = ({ icon, title, desc }: { icon: any, title: string, desc: string }) => (
  <Stack spacing={2} sx={{ p: 4, bgcolor: 'white', border: '1px solid #e2e8f0', borderRadius: 4, height: '100%' }}>
    <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
      {icon}
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
    <Typography variant="body2" color="text.secondary">{desc}</Typography>
  </Stack>
);

const ContactDetail = ({ icon, title, content }: any) => (
  <Grid item xs={12} sm={6}>
    <Stack direction="row" spacing={1.5}>
      <Box sx={{ color: 'primary.main' }}>{icon}</Box>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{content}</Typography>
      </Box>
    </Stack>
  </Grid>
);

export default AboutPage;
